import { amountToNumber } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import type { UnitType } from 'modules/payroll_bundle/store/models/Supplement'

import type { SupplementAmount, SupplementCurrencyAmount } from './types'

const AMOUNT_SEPARATOR_CHAR = '|'

function filterSupplementAmountsByUnit(
  supplementAmounts: SupplementAmount[],
  unit: UnitType
): SupplementAmount[] {
  return supplementAmounts.filter((supplementAmount) => supplementAmount.units === unit)
}

function getTotalMoneyFormatted(supplementAmounts: SupplementAmount[]): string | null {
  // It gets the supplements where the units property equals to 'money'
  const moneySupplementAmounts = filterSupplementAmountsByUnit(
    supplementAmounts,
    'money'
  ) as SupplementCurrencyAmount[]

  if (!moneySupplementAmounts || moneySupplementAmounts.length === 0) {
    return null
  }

  // Calculates the total per each currency
  const supplementAmountsByCurrency = moneySupplementAmounts.reduce<Record<string, number>>(
    (totalsByCurrency, { currency, amount }) => {
      const total = totalsByCurrency[currency]
      const amountNumber = amountToNumber(amount)

      return { ...totalsByCurrency, [currency]: total ? total + amountNumber : amountNumber }
    },
    {}
  )

  // Formats the totals of every currency to amount including the currency symbol
  return Object.keys(supplementAmountsByCurrency)
    .sort()
    .map((currency) =>
      i18n.formatNumber(supplementAmountsByCurrency[currency] as number, 'currency', currency)
    )
    .join(` ${AMOUNT_SEPARATOR_CHAR} `)
}

function getTotalUnitsFormatted(supplementAmounts: SupplementAmount[]): string | null {
  // It gets the supplements where the units property equals to 'units'
  const unitSupplementAmounts = filterSupplementAmountsByUnit(supplementAmounts, 'units')
  const totalAmount = unitSupplementAmounts.reduce(
    (total, supplementAmount) => amountToNumber(supplementAmount.amount) + total,
    0
  )

  return totalAmount > 0
    ? i18n.tp('compensation.unit.units', { count: Math.round(totalAmount) })
    : null
}

/**
 * It calculates the supplement total amount and returns a formatted value. It calculates the total per currency and per units, so
 * in a situation where a user added supplements with different currencies and units, it will show something like:
 *
 * $12,345 | 1345 Units | €4314
 *
 * The pipe character is used to separate the total amounts
 */
export default function getTotalAmountFormatted(
  supplementAmounts: SupplementAmount[]
): string | null {
  if (supplementAmounts.length === 0) {
    return null
  }

  const totalUnitsFormatted = getTotalUnitsFormatted(supplementAmounts)
  const totalMoneyFormatted = getTotalMoneyFormatted(supplementAmounts)

  return [totalMoneyFormatted, totalUnitsFormatted]
    .filter((formattedAmount) => formattedAmount !== null)
    .join(` ${AMOUNT_SEPARATOR_CHAR} `)
}
